import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { blogs } from "../data/site";
import { staggerCardDelay, staggerDelay } from "../lib/stagger";
import { useCallback, useRef } from "react";

function useCardGlow() {
  const ref = useRef<HTMLAnchorElement>(null);
  const onMove = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    el.style.setProperty("--mouse-x", `${x}%`);
    el.style.setProperty("--mouse-y", `${y}%`);
  }, []);
  return { ref, onMove };
}

function BlogCard({
  post,
  delay,
}: {
  post: (typeof blogs)[number];
  delay: number;
}) {
  const { ref, onMove } = useCardGlow();
  return (
    <Link
      ref={ref}
      onMouseMove={onMove}
      to={`/blogs/${post.slug}`}
      className="card-glow stagger-in group flex flex-col gap-3 rounded-xl border border-border bg-card/50 p-6 backdrop-blur-sm"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between">
        <h2 className="text-lg font-semibold text-foreground transition-colors group-hover:text-primary">
          {post.title}
        </h2>
        <ArrowRight className="mt-1 h-4 w-4 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0.5 group-hover:text-primary group-hover:opacity-100" />
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">{post.summary}</p>
      {post.tags && post.tags.length > 0 ? (
        <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
          {post.tags.map((t) => (
            <span
              key={t}
              className="rounded-md bg-secondary/80 px-2.5 py-0.5 text-xs text-muted-foreground"
            >
              {t}
            </span>
          ))}
        </div>
      ) : null}
    </Link>
  );
}

export default function Blogs() {
  return (
    <div className="flex flex-col gap-12 py-20 md:py-28">
      <div className="flex flex-col gap-3">
        <h1
          className="stagger-in font-display text-4xl font-bold tracking-tight sm:text-5xl"
          style={{ animationDelay: staggerDelay(0) }}
        >
          Blog
        </h1>
      </div>

      {blogs.length === 0 ? (
        <p
          className="stagger-in text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground"
          style={{ animationDelay: staggerDelay(2) }}
        >
          Coming soon...
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {blogs.map((post, i) => (
            <BlogCard key={post.slug} post={post} delay={staggerCardDelay(i)} />
          ))}
        </div>
      )}
    </div>
  );
}
